/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Component to show-hide bottom menu
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.3.0
 */

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { pushGTMAnalytic } from '../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../modules/analytics/GoogleTagManager/GTMOperations';
import {
    EGTMBasketSectionType,
    EGTMBottomMenuButtonTitleType,
} from '../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { IGTMPushAnalyticPayload } from '../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { makeSelectTranslationCommon } from '../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../modules/internationalization/types/common';
import { IRootReducer } from '../../../reducers/rootReducer';
import { BlockWithIcon } from '../index';
import { TableFooterOpenHandlerStyled, TableFooterShowMenuStyled } from './styled/TableFooterStyled';

interface ITableFooterShowMenuProps {
    gtmSectionType?: EGTMBasketSectionType;
    isOpen: boolean;
    onClick?: () => void;
}

interface IDispatchProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export type TTableFooterShowMenuProps = ITableFooterShowMenuProps & IConnectedProps & IDispatchProps;

export const TableFooterShowMenu = (props: TTableFooterShowMenuProps) => {
    const handleClick = () => {
        if (props.onClick) props.onClick();
        if (props.gtmSectionType) props.pushGtmAnalytic({
            operation: GTMOperations.basket.onBottomMenuButtonClick(props.gtmSectionType, props.isOpen ?
                EGTMBottomMenuButtonTitleType.HIDE : EGTMBottomMenuButtonTitleType.SHOW),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        })
    };

    return (
        <TableFooterOpenHandlerStyled
            className={props.isOpen ? 'table-footer__open-handler open' : 'table-footer__open-handler'}
            style={{ right: 40 }}
            onClick={handleClick}
        >
            <BlockWithIcon iconRight={icons.arrowDownSmallWhiteIcon} iconRightSize={20}>
                {
                    !props.isOpen &&
                    <TableFooterShowMenuStyled>
                        {props.commonTranslation.footer.showMenu}
                    </TableFooterShowMenuStyled>
                }
            </BlockWithIcon>
        </TableFooterOpenHandlerStyled>
    )
}

export interface IConnectedProps {
    commonTranslation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    pushGtmAnalytic: (payload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TableFooterShowMenu);
